/**
 * Matterport Showcase URL helpers.
 *
 * Pure, synchronous utilities. Kept outside `lib/actions/*` on
 * purpose — any module with `"use server"` may only export async
 * functions, and these parsers are used both server-side (the
 * create-scan action) and client-side (live-validate the URL
 * input before submit).
 */

import { hostMatchesDomain } from "@/lib/url-host";

/**
 * Matterport model SIDs are case-sensitive alphanumerics. Most are
 * 10-12 chars, but we accept 6-32 so the occasional shorter dev /
 * demo SID (`GTbSvUEnRp`, etc.) still parses — Matterport itself
 * decides whether the SID resolves to a real model at render time.
 */
export const MATTERPORT_SID_REGEX = /^[A-Za-z0-9]{6,32}$/;

/**
 * Deliverable types whose customer artifact IS a Matterport tour.
 * Re-exported from `components/virtualspace/matterport-brand` for the
 * brand-mark callers; lives here so server loaders can gate on it
 * without pulling React components into their module graph.
 */
export function isMatterportDeliverableType(type: string): boolean {
  return (
    type === "matterport_scan" ||
    type.startsWith("matterport_") ||
    type === "digital_twin"
  );
}

/**
 * True when a URL points at Matterport Showcase (any `*.matterport.com`
 * host). Used to detect a cross-wired link — e.g. an operator pasting the
 * sibling Matterport tour into an Aerial capture row's output URL — so a
 * customer surface can suppress it instead of sending the customer into
 * the wrong product's viewer.
 */
export function isMatterportShowcaseUrl(
  url: string | null | undefined,
): boolean {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    const host = parsed.hostname.toLowerCase();
    return host === "matterport.com" || host.endsWith(".matterport.com");
  } catch {
    return false;
  }
}

/**
 * A Matterport Showcase link is only ever the customer artifact of a
 * Matterport-kind deliverable. When it lands on another type's
 * `output_url` — an Aerial capture row carrying the sibling tour link is
 * the case we've seen in the wild — the customer clicks that row's CTA and
 * ends up in the tour instead of their own files.
 */
export function isCrossWiredMatterportOutputUrl(args: {
  deliverableType: string;
  outputUrl: string | null | undefined;
}): boolean {
  if (isMatterportDeliverableType(args.deliverableType)) return false;
  return isMatterportShowcaseUrl(args.outputUrl);
}

/**
 * Accepts the four shapes we see in the wild and normalizes every
 * one down to the SID plus a canonical URL:
 *
 *   https://my.matterport.com/show/?m=SxQL3iGyoDo
 *   https://matterport.com/show/?m=SxQL3iGyoDo&play=1
 *   my.matterport.com/show/?m=SxQL3iGyoDo       (no scheme)
 *   SxQL3iGyoDo                                 (bare SID)
 *
 * Returns null for anything that isn't a Matterport Showcase URL or
 * a parseable SID. Callers should treat null as "invalid input" and
 * surface a UX error — don't fall back to the raw string.
 */
export function parseMatterportUrl(
  input: string,
): { sid: string; url: string } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Bare SID — "just paste the id" is a common expectation.
  if (MATTERPORT_SID_REGEX.test(trimmed)) {
    return {
      sid: trimmed,
      url: `https://my.matterport.com/show/?m=${trimmed}`,
    };
  }

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  if (!hostMatchesDomain(host, "matterport.com")) return null;

  const sidParam = parsed.searchParams.get("m");
  if (!sidParam || !MATTERPORT_SID_REGEX.test(sidParam)) return null;

  return {
    sid: sidParam,
    // Canonical URL — sheds scheme/host variants and extra query
    // params so dedupe on (provider, provider_model_id) stays sane.
    url: `https://my.matterport.com/show/?m=${sidParam}`,
  };
}

/**
 * Extract a Matterport model id from EITHER a full showcase
 * URL (`https://my.matterport.com/show/?m=<id>...`) or an
 * already-bare `provider_model_id`. Returns null when neither
 * looks usable — the caller should bail out of rendering an
 * iframe rather than show a broken one.
 */
export function extractShowcaseModelId(
  showcaseLink: string | null | undefined,
  fallbackId: string | null | undefined,
): string | null {
  if (showcaseLink) {
    try {
      const url = new URL(showcaseLink);
      const m = url.searchParams.get("m");
      if (m && m.trim()) return m.trim();
    } catch {
      // Fall through to the fallback id.
    }
  }
  const id = (fallbackId ?? "").trim();
  return id || null;
}

/**
 * Build a deep-link showcase URL that lands the camera on a
 * specific sweep AND aims it at a specific bearing. Used by
 * the public SpaceSight viewer, the Survey Gallery equipment
 * spreadsheet, and anywhere else we want a "here's the exact
 * spot" link that works outside our app (email, Slack, etc.).
 *
 * Matterport's URL conventions (undocumented on their public
 * help pages but consistent with `sdk.Link.createDeepLink()`
 * output and thousands of shared links in the wild):
 *
 *   - `m=<model_id>`   — model sid, hex
 *   - `play=1`         — auto-enter the model (skip splash)
 *   - `qs=1`           — skip Intro/Spatial Orientation Screen
 *   - `ss=<index>`     — starting scan, 1-based INTEGER index
 *                        into the ordered sweeps list. NOTE:
 *                        this is NOT the hex sweep SID — that
 *                        gets silently rejected and the viewer
 *                        falls back to the default landing
 *                        view. We emit `ss` only when we have
 *                        a numeric index on file (migration
 *                        202 introduced `pano_index`).
 *   - `sr=<pitch>,<yaw>` — starting rotation, RADIANS, pitch
 *                          first. Positive pitch = looking
 *                          down; positive yaw = clockwise
 *                          from above (right-turning).
 *
 * Accepts:
 *   - `panoIndex` — 0-based position of the sweep in the
 *     model's ordered sweeps list. When null the `ss` param
 *     is OMITTED and the viewer lands on whatever sweep
 *     Matterport picks as the default. Still better than
 *     emitting an invalid `ss=<hex>` which breaks the whole
 *     deep-link.
 *   - `panoRotationYaw` + `bearingPhi` — world-frame yaw +
 *     pano-local azimuth (radians). World yaw of the object
 *     = `panoRotationYaw + bearingPhi`.
 *   - `bearingTheta` — pano-local elevation (radians).
 *     Positive in our convention = looking UP. Matterport's
 *     URL pitch convention is positive = looking DOWN, so
 *     we NEGATE for the URL.
 */
export function buildShowcaseDeepLink(input: {
  modelId: string;
  panoIndex: number | null;
  panoRotationYaw: number | null;
  bearingPhi: number | null;
  bearingTheta: number | null;
}): string {
  const params = new URLSearchParams({
    m: input.modelId,
    play: "1",
    qs: "1",
  });
  // `ss` is a 1-based integer index, not a sweep SID. Only
  // emit when we actually have the index on hand — emitting a
  // hex sid here breaks the deep-link entirely (Matterport
  // drops both `ss` AND `sr` and shows the default view).
  if (
    input.panoIndex != null &&
    Number.isFinite(input.panoIndex) &&
    input.panoIndex >= 0
  ) {
    params.set("ss", String(input.panoIndex + 1));
  }
  if (input.bearingPhi != null && Number.isFinite(input.bearingPhi)) {
    // bearing_phi is already in world frame — Matterport's
    // takeEquirectangular renders world-aligned panos, so the
    // bbox X-centre → phi computation is world yaw directly.
    // Historical code here added panoRotationYaw, but that
    // column was always populated with the camera's view
    // direction at capture time (π radians on every live row),
    // rotating every deep-link aim 180° from the real target
    // — the "public viewer always faces the wrong way" bug.
    // See `bearingToWorldDirection` for the full reasoning.
    void input.panoRotationYaw;
    const yaw = input.bearingPhi;
    // Matterport's URL pitch convention is positive = down,
    // the opposite of our bearing_theta convention (positive
    // = up). Flip the sign so clicking a detection aims where
    // the operator expects.
    const pitch = -(input.bearingTheta ?? 0);
    params.set("sr", `${pitch.toFixed(3)},${yaw.toFixed(3)}`);
  }
  return `https://my.matterport.com/show/?${params.toString()}`;
}

/**
 * Build a Matterport Showcase embed URL with the standard "clean
 * embed" toggles applied.
 *
 * Used by the public VirtualSpace renderer when a Matterport block
 * is the dominant media in its Space — we want the iframe to read
 * as a focused viewer, not as a full Matterport.com landing page
 * with every share / VR / help affordance the standalone product
 * surfaces. The Share + VR buttons in particular are confusing in
 * an embedded "the show" treatment because clicking them lands the
 * user OUTSIDE the Spatialist VirtualSpace context.
 *
 * Matterport's documented URL parameters (from
 * https://support.matterport.com/s/article/URL-Parameters):
 *
 *   - `m=<sid>`     — model id (required)
 *   - `play=1`      — auto-enter the model (skip splash)
 *   - `qs=1`        — quickstart, skip the Spatial Orientation Screen
 *   - `share=0`     — hide the Share button in the toolbar
 *   - `vr=0`        — hide the VR / Cardboard button
 *   - `hr=0`        — hide the Highlight Reel button
 *   - `help=0`      — hide the Help / Info button
 *   - `mls=2`       — MLS-friendly chrome strip (real-estate convention):
 *                     removes branding, in-tag links, VR, Business Card,
 *                     Quick Link AND the top-left "About" panel after load.
 *                     Documented for the MLS use case but works across
 *                     every embed and matches our "drop the visitor on
 *                     the model, not the Matterport.com landing page"
 *                     intent. `mls=1` is the same minus the About-panel
 *                     suppression — we always want the stronger one.
 *
 * Defaults turn ALL FIVE hide-toggles on (= clean embed). Pass
 * `hideShare: false` etc. to opt back in to a specific affordance —
 * a future per-block `share_links_in_viewer` config can thread
 * through here without changing the renderer's call site.
 *
 * Returns null for an empty / whitespace-only SID — callers should
 * treat null as "render the not-set placeholder instead of a broken
 * iframe", consistent with `extractShowcaseModelId` and
 * `parseMatterportUrl` above.
 *
 * Distinct from `buildShowcaseDeepLink` above: that helper composes
 * a "land on this exact sweep at this exact bearing" link for the
 * SpaceSight detector + Survey Gallery, where preserving the full
 * Showcase chrome IS the point (operators want to click through to
 * Matterport's own UI). This helper strips chrome for embed-mode.
 */
export function buildMatterportEmbedUrl(
  modelId: string,
  opts: {
    hideShare?: boolean;
    hideVr?: boolean;
    hideHelp?: boolean;
    hideHighlightReel?: boolean;
    hideMlsChrome?: boolean;
    /**
     * `brand=0` — Matterport URL param that hides the "Powered by
     * Matterport" wordmark cluster in the bottom-right of the
     * viewer (the same cluster mpskin / VirtuoArt suppress for
     * the cleaner overlay look). Documented at
     * https://support.matterport.com/s/article/URL-Parameters.
     * Default true so embed-mode Launches look minimal; defer
     * to Matterport's account-tier enforcement for whether the
     * suppression actually takes effect (free-tier embeds may
     * ignore brand=0 + still show the wordmark — known Matterport
     * behavior; mls=2 has the same limitation).
     */
    hideBranding?: boolean;
  } = {},
): string | null {
  const trimmed = modelId.trim();
  if (!trimmed) return null;

  const {
    hideShare = true,
    hideVr = true,
    hideHelp = true,
    hideHighlightReel = true,
    hideMlsChrome = true,
    hideBranding = true,
  } = opts;

  // URLSearchParams encodes the SID + escapes any special chars;
  // Matterport SIDs are A-Za-z0-9 in practice (see MATTERPORT_SID_REGEX)
  // but defending against a future provider that emits something
  // exotic costs us nothing.
  const params = new URLSearchParams({
    m: trimmed,
    play: "1",
    qs: "1",
  });
  if (hideShare) params.set("share", "0");
  if (hideVr) params.set("vr", "0");
  if (hideHighlightReel) params.set("hr", "0");
  if (hideHelp) params.set("help", "0");
  if (hideMlsChrome) params.set("mls", "2");
  if (hideBranding) params.set("brand", "0");

  return `https://my.matterport.com/show/?${params.toString()}`;
}

/**
 * Public Matterport thumbnail for any model id — the stable
 * `my.matterport.com/api/v1/player/models/<sid>/thumb` endpoint the
 * Showcase + embeds use. 302-redirects to a freshly-signed CDN image on
 * every request (URL never expires). Works for public models without API
 * credentials.
 */
export function matterportThumbnailUrl(
  modelId: string | null | undefined,
  width = 480,
): string | null {
  const id = (modelId ?? "").trim();
  if (!id) return null;
  return `https://my.matterport.com/api/v1/player/models/${encodeURIComponent(id)}/thumb?width=${width}&dpr=1&disable=upscale`;
}
